import React, { useEffect, useRef } from "react";
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";

// Register required Chart.js components
Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const PublicationBarChart = ({ publicationData }) => {
    console.log("Filtered data:", publicationData);
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const totalCount = Object.values(publicationData).reduce((acc, curr) => acc + curr, 0);

    useEffect(() => {
        const renderChart = () => {
            const ctx = chartRef.current.getContext("2d");

            // Destroy previous chart instance if it exists
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }

            // Filter out any publication data where the count is 0
            const filteredData = Object.entries(publicationData).filter(([key, value]) => value > 0);
            const filteredLabels = filteredData.map(([key]) => key);
            const filteredValues = filteredData.map(([, value]) => value);

            if (filteredLabels.length === 0) {
                // If there are no valid data points, do not render the chart
                console.log("No valid data to render");
                return;
            }

            // Calculate dynamic max value based on filtered publication counts
            const maxCount = Math.max(...filteredValues) * 1.2; // Adding some padding

            // Create a new chart instance
            chartInstanceRef.current = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: filteredLabels,
                    datasets: [
                        {
                            label: "Publication Count",
                            data: filteredValues,
                            backgroundColor: [
                                "#2A9D8F", // Teal Green  
                                "#457B9D", // Deep Blue  
                                "#F4A261", // Warm Orange  
                                "#8D6AB8", // Muted Purple  
                                "#F77F00", // Bright Amber  
                                "#6D597A", // Elegant Plum  
                                "#83C5BE", // Light Aqua  
                                "#B56576", // Vintage Rose  
                                "#264653", // Dark Cyan  
                                "#5E60CE",  // Vivid Indigo  
                                 // New colors
                              "#FF6F61", // Coral Red  
                               "#FFD700", // Golden Yellow  
                               "#00B4D8", // Vivid Turquoise  
                               "#D9BF77", // Mustard Yellow  
                               // Soft Beige  
                              "#FF3D7F", // Hot Pink  
                              "#D4A5A5", // Dusty Pink  
                             
                            ],
                            
                            borderColor: [
                                "#FF6384",
                                "#36A2EB",
                                "#FFCE56",
                                "#4BC0C0",
                                "#9966FF",
                                "#FF9F40",
                            ],
                            barThickness: 40, 
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            beginAtZero: true,
                            max: maxCount, // Dynamically adjust the max value
                        },
                    },
                    plugins: {
                        tooltip: {
                            enabled: true, // Enable tooltips
                        },
                    },
                },
                plugins: [
                    {
                        id: "valueInsideBar",
                        afterDatasetsDraw: (chart) => {
                            const { ctx } = chart;
                            chart.data.datasets.forEach((dataset, i) => {
                                const meta = chart.getDatasetMeta(i);
                                meta.data.forEach((bar, index) => {
                                    const value = dataset.data[index];
                                    ctx.fillStyle = "#fff"; // Text color inside the bar
                                    ctx.font = "12px Arial"; // Text font and size
                                    ctx.textAlign = "center";
                                    ctx.textBaseline = "middle";
                                    // Position the text inside the bar
                                    const positionX = bar.x;
                                    const positionY = bar.y + (bar.height / 2);
                                    ctx.fillText(value, positionX, positionY);
                                });
                            });
                        },
                    },
                ],
            });
        };

        // Only render chart if there is valid data
        if (Object.keys(publicationData).length > 0) {
            renderChart();
        }

        return () => {
            // Cleanup chart instance when the component unmounts
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [publicationData]);

    return (
        <div style={{ height: "500px", width: "100%" }}>
             <h2>Total Publication Count: {totalCount}</h2> {/* Display total count */}
            <canvas ref={chartRef}></canvas>
        </div>
    );
};

export default PublicationBarChart;
